<template>
  <div id="footer-container">
    <div id="blue-section">
      <div class="container">
        <div class="row w-100">
          <div class="col-12 col-md-4">
            <div>Settle in Estonia</div>

            <div class="pretty-link">
              <a href="/gdpr" target="_blank">{{ t("PRIVACY_POLICY") }}</a>
            </div>

            <div class="pretty-link">
              <a href="mailto:info@settleinestonia.ee">{{ t("EMAIL") }}: info@settleinestonia.ee</a>
            </div>

            <div class="pretty-link">
              <a href="https://www.facebook.com/settleinestonia" target="_blank">Facebook</a>
            </div>

            <div class="pretty-link">
              <a :href="localeSpecificUrlFromMap('https://www.eesti.ee/syndmusteenused', stateProvidedServicesMap)" target="_blank">{{ t("STATE_PROVIDED_SERVICES") }}</a>
            </div>

            <div class="mt-3">
              <div class="fw-bold h6">{{ t("FOOTER_TRAINING_MATERIALS") }}:</div>

              <ul class="ps-3 mt-2">
                <li class="pretty-link">
                  <a href="https://issuu.com/settleinestonia" target="_blank">ISSUU</a>
                </li>
                <li class="pretty-link">
                  <a href="https://www.youtube.com/@settleestonia1437" target="_blank">YouTube</a>
                </li>
                <li class="pretty-link">
                  <a :href="localeSpecificUrlFromMap('https://e-koolikott.ee', eKoolikottMap)" target="_blank">E-koolikott</a>
                </li>
              </ul>
            </div>
          </div>

          <div class="col-12 col-md-4">
            <div>{{ t("FOOTER_IMPORTANT_CONTACTS") }}</div>

            <div>
              <div class="fw-bold">
                <span v-if="locale === Language.RUS.toString()">{{ t("FOOTER_INFORMATION_SERVICE") }} {{ t("FOOTER_POLICE") }}: 1247</span>
                <span v-else>{{ t("FOOTER_POLICE") }} {{ t("FOOTER_INFORMATION_SERVICE") }}: 1247</span>
              </div>
              <a :href="localeSpecificUrlFromMap('https://www.politsei.ee', languages)" target="_blank" class="subtext pretty-link">{{ t("MORE_INFO_ONLINE") }}</a>
            </div>

            <div class="pretty-link">
              <a :href="localeSpecificUrlFromMap('https://www.politsei.ee', migrationConsultantsMap)" target="_blank">{{ t("MIGRATION_CONSULTANTS") }}</a>
            </div>

            <div class="mt-2">
              <span class="fw-bold">{{ t("STATE_INFORMATION_SERVICE") }}: 1247</span>, {{ t("ABROAD_DIAL") }}: (+372)&nbsp;600&nbsp;1247
            </div>

            <div class="pretty-link">
              <a :href="localeSpecificUrlFromMap('https://www.kriis.ee', languages)" target="_blank">{{ t("STATE_CRISIS_INFO_PORTAL") }}&nbsp;Kriis.ee</a>
            </div>
          </div>

          <div class="col-12 col-md-4">
            <div>{{ t("EMERGENCY_CONTACTS") }}</div>

            <div class="fw-bold">{{ t("EMERGENCY_CONTACT") }}: 112</div>

            <div>
              <span class="fw-bold">{{ t("CHILD_HELPLINE") }}: 116&nbsp;111</span>, {{ t("ABROAD_DIAL") }}: (+372)&nbsp;600&nbsp;4434
              <a :href="localeSpecificUrlFromMap('https://www.lasteabi.ee', languages)" target="_blank" class="subtext pretty-link">{{ t("MORE_INFO_ONLINE") }}</a>
            </div>

            <div>
              <span class="fw-bold">{{ t("VICTIM_ASSISTANCE") }}: 116&nbsp;006</span>
              <a :href="localeSpecificUrlFromMap('https://www.palunabi.ee', victimAssistanceMap)" target="_blank" class="subtext pretty-link">{{ t("MORE_INFO_ONLINE") }}</a>
            </div>

            <div>
              <span class="fw-bold">{{ t("WOMEN_HELPLINE") }}: 1492</span>
              <a :href="localeSpecificUrlFromMap('https://naisteliin.ee', womenHelplineMap)" target="_blank" class="subtext pretty-link">{{ t("MORE_INFO_ONLINE") }}</a>
            </div>

            <div>
              <span class="fw-bold">{{ t("FAMILY_DOCTOR_ADVICE_LINE") }}: 1220</span>, {{ t("ABROAD_DIAL") }}: (+372)&nbsp;634&nbsp;6630
              <a :href="localeSpecificUrlFromMap('https://www.1220.ee', familyDoctorMap)" target="_blank" class="subtext pretty-link">{{ t("MORE_INFO_ONLINE") }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="text-center py-3">
      <img class="footer-logo"
           src="@/assets/settle_estonia_horizontal_negative.svg"
           alt="Settle in Estonia logo"
      />
      <img class="footer-logo"
           src="@/assets/kultuurimin_3lovi_eng_pms.svg"
           alt="Ministry of Culture logo"
      />
      <img class="footer-logo"
           src="@/assets/co_funded_by_eu_logo.svg"
           alt="Co-funded by European Union logo"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
#blue-section {
  background-color: var(--p-color-blue-600);
  padding-bottom: 50px;
  padding-top: 50px;
}

.footer-logo {
  max-width: max(275px, 12vw);
}

#footer-container {
  ul {
    list-style: none;
  }

  .row > div {
    & > div {
      font-size: 14px;
      color: #FFFFFF;
      margin-bottom: 0.6rem;

      & a, .h6 {
        color: #FFFFFF;
      }

      & > .subtext {
        font-size: 12px;
        display: block;
        text-decoration: underline;
      }
    }

    & > div:first-child {
      font-size: 24px;
    }
  }
}

.pretty-link {
  & > a {
    text-decoration: underline;
  }

  &::before {
    content: '>';
    position: relative;
    display: inline-block;
    transform: scaleY(1.25);
    margin-right: 0.5rem;
    font-weight: bold;
    font-size: 12px;
  }
}
</style>
<script setup lang="ts">
import {useI18n} from "vue-i18n";
import {Language} from "@/model/language";

const {t, locale} = useI18n();

const languages = {
  [Language.EST]: "/et",
  [Language.ENG]: "/en",
  [Language.RUS]: "/ru",
};

const eKoolikottMap = {
  [Language.EST]: "/et/search?q=settle%20&minAdded=2009&sort=relevance",
  [Language.ENG]: "/en/search?q=settle%20&minAdded=2009&sort=relevance",
  [Language.RUS]: "/ru/search?q=settle%20&minAdded=2009&sort=relevance",
};

const stateProvidedServicesMap = {
  [Language.EST]: "/et/eestis-kohanemine",
  [Language.ENG]: "/en/settling-in-estonia",
  [Language.RUS]: "/ru/adaptatsiia-v-estonii",
};

const migrationConsultantsMap = {
  [Language.EST]: "/et/migratsiooninoustajad",
  [Language.ENG]: "/en/migration-consultants",
  [Language.RUS]: "/ru/konsultanty-po-migracii",
};

const victimAssistanceMap = {
  [Language.EST]: "/et/vota-uhendust",
  [Language.ENG]: "/en/contact-us",
  [Language.RUS]: "/ru/kontakty",
};

const womenHelplineMap = {
  [Language.EST]: "?keel=1",
  [Language.ENG]: "?keel=2",
  [Language.RUS]: "?keel=3",
};

const familyDoctorMap = {
  [Language.EST]: "/et",
  [Language.RUS]: "/ru/zvonki",
};

const localeSpecificUrlFromMap = (url: string, languageMap: object) => {
  const suffix = languageMap[locale.value];
  return suffix ? (url + suffix) : url;
};
</script>
